// ============================================================
// FeedbackFAB — Floating Action Button persistent en bas-droite
// ============================================================
// Le BetaBanner haut a déjà un bouton "Remonter un bug" mais il sort
// du viewport au scroll. Ce FAB reste accessible partout et pulse
// périodiquement (toutes les ~90s) pour rappeler discrètement son
// existence — sans être intrusif.
//
// Auto-trigger : si une erreur récente a été dispatchée via
//   window.dispatchEvent(new CustomEvent('merlin:user-error', { detail: { msg } }))
// le bouton passe en mode "pulse rouge" pendant 30s pour inciter
// au feedback à chaud.
//
// Cf. ANALYSE_USERS_2026-05-04 : 2 feedback / 57 users = 3.5% taux.
// Objectif : >10% pour avoir un signal exploitable.
// ============================================================

import { useState, useEffect } from 'react';
import FeedbackModal from './FeedbackModal';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';

export default function FeedbackFAB() {
  const { user } = useAuth();
  const { profile } = useApp();
  const [modalOpen, setModalOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [errorMode, setErrorMode] = useState(false);

  // Pulse périodique discret (3 pulses puis silence ~90s)
  useEffect(() => {
    if (!user) return;
    const tick = () => {
      setPulse(true);
      setTimeout(() => setPulse(false), 4500);
    };
    const initial = setTimeout(tick, 12000);
    const interval = setInterval(tick, 90000);
    return () => { clearTimeout(initial); clearInterval(interval); };
  }, [user]);

  // Auto-trigger sur erreur récente — écoute l'event bus
  useEffect(() => {
    function onError() {
      setErrorMode(true);
      setTimeout(() => setErrorMode(false), 30000);
    }
    window.addEventListener('merlin:user-error', onError);
    return () => window.removeEventListener('merlin:user-error', onError);
  }, []);

  if (!user) return null;

  const baseColor = errorMode ? '#dc2626' : '#fe8204';
  const baseShadow = errorMode ? 'rgba(220,38,38,0.45)' : 'rgba(254,130,4,0.35)';

  return (
    <>
      <div className="fixed bottom-5 right-5 z-[9400] pointer-events-none">
        {/* Pulse aura */}
        <div
          className={`absolute inset-0 rounded-full transition-all ${(pulse || errorMode) ? 'animate-fab-ping' : ''}`}
          style={{ background: baseColor, opacity: 0 }}
          aria-hidden
        />
        <button
          onClick={() => setModalOpen(true)}
          className="pointer-events-auto relative w-14 h-14 rounded-full text-white flex items-center justify-center font-bold transition-all hover:scale-110 active:scale-95"
          style={{
            background: `linear-gradient(135deg, ${baseColor} 0%, ${errorMode ? '#ef4444' : '#f59e0b'} 100%)`,
            boxShadow: `0 8px 22px ${baseShadow}`,
          }}
          aria-label="Donner un retour ou signaler un bug"
          title={errorMode ? 'Tu as eu un souci ? Dis-nous' : 'Un retour ? Un bug ? Une idée ?'}
        >
          <span className="text-2xl">{errorMode ? '⚠️' : '💬'}</span>
        </button>

        {/* Bulle d'aide pulse */}
        {(pulse || errorMode) && (
          <div className="absolute right-[64px] top-1/2 -translate-y-1/2 whitespace-nowrap pointer-events-none">
            <div
              className="px-3 py-1.5 rounded-xl text-xs font-bold animate-in"
              style={{
                background: errorMode ? '#dc2626' : 'white',
                color: errorMode ? 'white' : '#1a1a2e',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                border: errorMode ? 'none' : '1px solid rgba(0,0,0,0.06)',
              }}
            >
              {errorMode ? 'Tu as eu un souci ? Dis-nous →' : 'Un retour ? Un bug ?'}
            </div>
          </div>
        )}
      </div>

      <FeedbackModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        user={user}
        profile={profile}
        contextData={{ triggered_by: errorMode ? 'auto_error' : 'manual_fab' }}
      />

      <style>{`
        @keyframes fab-ping {
          0%   { transform: scale(0.95); opacity: 0.45; }
          70%  { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .animate-fab-ping {
          animation: fab-ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </>
  );
}
